import type { Socket } from 'socket.io-client';
import type { CanonicalPrivateEvent, PrivateEventName } from '@/lib/realtime/contracts';
import type { PrivateEventBus } from '@/lib/realtime/private/private-event-bus';

const PRIVATE_EVENT_NAMES: readonly PrivateEventName[] = [
  'order:placed',
  'order:filled',
  'order:partially-filled',
  'order:cancelled',
  'position:updated',
  'trade:executed',
  'pnl:updated',
  'balance:changed',
  'notification:created',
] as const;

function hasPlaeeEventId(payload: unknown): payload is { plaeeEventId: string } {
  return (
    payload !== null &&
    typeof payload === 'object' &&
    'plaeeEventId' in payload &&
    typeof (payload as { plaeeEventId: unknown }).plaeeEventId === 'string'
  );
}

/**
 * Wires the existing Socket.IO `/realtime` connection into the {@link PrivateEventBus}
 * as the `backend` source. The realtime gateway emits every private event on
 * its canonical name (e.g. `'order:filled'`) once the player JWT auth path
 * (phase 6) joins the user-room, so this listener is a 1:1 pass-through.
 *
 * Returns an uninstall function so callers (or tests) can detach.
 */
export function installSocketIoPrivateIngest(socket: Socket, bus: PrivateEventBus): () => void {
  const handlers = new Map<PrivateEventName, (payload: unknown) => void>();

  for (const name of PRIVATE_EVENT_NAMES) {
    const handler = (payload: unknown): void => {
      if (!hasPlaeeEventId(payload)) return;
      bus.ingest({
        name,
        payload: payload as CanonicalPrivateEvent<typeof name>,
        source: 'backend',
        plaeeEventId: payload.plaeeEventId,
      });
    };
    handlers.set(name, handler);
    socket.on(name, handler);
  }

  return () => {
    for (const [name, handler] of handlers) {
      socket.off(name, handler);
    }
  };
}
